import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import ReactPlayer from 'react-player/lazy';

import css from './mainPage.module.scss';
import PageLayout, { Image, Photo } from '../../components/layout/pageLayout/PageLayout';
import { Elem } from '../../components/types';
import Button, { ContainButton, OutlineButton, RightArrowButton } from '../../components/buttons/Buttons';
import { UnderlinedText } from '../../components/texts/Texts';
import { PlayIcon, Video1Icon, Video2Icon, VideoBorder } from '../../components/Icons';
import ProgramBlock from './programBlock/ProgramBlock';
import { useIsMobile, usePageMargin } from '../../components/layout/responsive';
import { GALLERY_PAGE, PROGRAM_PAGE, SIGNUP_PAGE, SPEAKERS_PAGE } from '../../routes';
import SpeakerCard from '../speakers/SpeakerCard';
import { Swiper, SwiperSlide } from '../../components/elements/Swiper';
import {
  aboutSelector,
  fetchForumData,
  galleryImagesSelector,
  partnersSelector,
  programSelector,
  speakersSelector
} from './forumDataSlice';
import { Link, LocationRow } from '../../components/elements/Elements';
import { usePlural } from '../../localization/usePlural';
import { useMoment } from '../../hooks/moment';
import { useLoading, useScrollToTop } from '../../hooks/react';
import Dialog from '../../components/dialog/Dialog';
import { FOOTER } from '../../config/links';
import { Speaker } from '../../services/models';
import SpeakerDetailedModal from '../speakers/detailed/SpeakerDetailed';
import { GallerySwiper } from '../gallery/detailed/GalleryDetailed';
import { useLanguageChanged } from '../../hooks/localization';
import { useAppConfig } from '../../components/AppConfigProvider';


export default function MainPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { withLoading, loading } = useLoading();
  const program = useSelector(programSelector);
  const pageTitle = t('main.title');
  const fetchData = useCallback((force: boolean = false) => {
    const callback = async () => dispatch(fetchForumData(force));
    withLoading(callback());
  }, [dispatch, withLoading]);
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLanguageChanged(() => fetchData(true));
  useScrollToTop([loading]);
  return (
    <PageLayout className={classnames(css.MainPage)} pageTitle={pageTitle} isLoading={loading}>
      <MainBlock />
      <AboutBlock />
      {program && <ProgramBlock className={css.ProgramBlock} program={program} />}
      <SpeakerList />
      <Photos />
      <PartnerList />
    </PageLayout>
  );
}

function MainBlock() {
  const { t } = useTranslation();
  const appConfig = useAppConfig()
  const moment = useMoment();
  const isMobile = useIsMobile();
  const program = useSelector(programSelector);
  const lang = (new URLSearchParams(window.location.search)).get('lang');
  const modal = useModalState();
  const dates = program?.dates;
  const range = useMemo(() => {
    const d = dates?.map(i => moment(i.date).startOf('day'));
    if (!d || d.length === 0) return null;
    const min = d.reduce((a, b) => a < b ? a : b);
    const max = d.reduce((a, b) => a > b ? a : b);
    let title = min.format('DD');
    if (min !== max) title = `${title}-${max.format('DD')}`;
    return { title, subtitle: `${moment.localeData().months(min, 'D MMMM')}${min.format('\'YY')}` };
  }, [dates, moment]);

  if (program == null) return (<div className={css.MainBlock} />);
  const detailsBlock = (
    <Box display="flex" className={css.details}>
      {range && (<Box className={css.dates}>
        <Typography variant='h5' color='secondary'>{range.title}</Typography>
        <Typography variant='h3'>{range.subtitle}</Typography>
      </Box>)}
      {program?.location && (
        <LocationRow
          className={css.location}
          title={<Typography className={css.title} variant='subtitle1'>{(lang !== 'en') ? program?.location : program?.location_en}</Typography>}
          subtitle={<Typography className={css.subtitle}>{(lang !== 'en') ? program?.address : program?.address_en}</Typography>}
        />
      )}
    </Box>
  );
  const videoBlock = program?.main_video_link ? (
    <>
      <div className={css.MainBlock__videoWrap} onClick={modal.onOpen}>
        <Image className={css.video} src={program.main_video_preview!} alt="main video" />
        <VideoBorder className={css.videoBorder} />
        <Video1Icon className={css.videoIcon1} />
        <Video2Icon className={css.videoIcon2} />
        <div className={css.playBtnWrap}>
          <ContainButton aria-label="play" className={css.playBtn} color="secondary"><PlayIcon /></ContainButton>
          <Typography className={css.title} variant="subtitle1" color="inherit">{t('main.how_it_was')}</Typography>
        </div>
      </div>
      <VideoDialog src={program.main_video_link} open={modal.open} onClose={modal.onClose} />
    </>
  ) : null;
  return (
    <div className={css.MainBlock}>
      <div className={css.MainBlock__content}>
        <Typography className={css.title} variant='h1'>{(lang !== 'en') ? program?.title : program?.title_en}</Typography>
        <Typography className={css.subtitle} variant='h4' dangerouslySetInnerHTML={{ __html: (program?.theme) ? (lang !== 'en') ? program?.theme : program?.theme_en : '' }} />
        {!isMobile && detailsBlock}

        {isMobile && (
          <Box position="relative">
            {detailsBlock}
            {videoBlock}
          </Box>
        )}
        <div className={css.actions}>
          {appConfig.pages.get('signup') && <ContainButton href={SIGNUP_PAGE}>{t('main.sign_up')}</ContainButton>}
          <OutlineButton href={PROGRAM_PAGE}>{t('main.forum_program')}</OutlineButton>
        </div>
      </div>
      {!isMobile && videoBlock}
    </div>
  );
}

function AboutBlock() {
  const { t } = useTranslation();
  const { t: plural } = usePlural();
  const about = useSelector(aboutSelector);
  const modal = useModalState();
  const lang = (new URLSearchParams(window.location.search)).get('lang');
  const counts = [
    { title: about?.people_count, subtitle: plural('common.people', { count: about?.people_count ?? 0 }) },
    { title: about?.city_count, subtitle: plural('common.cities', { count: about?.city_count ?? 0 }) },
    { title: about?.country_count, subtitle: plural('common.countries', { count: about?.country_count ?? 0 }) },
  ].filter(i => i.title);
  if (!about || (!about.description && !about.video_link)) return null;

  let videoTitle = (about?.video_title) ? ((lang !== 'en') ? about?.video_title : about?.video_title_en) : '';
  let description = (about?.description) ? ((lang !== 'en') ? about?.description : about?.description_en) : '';
  return (
    <Box className={css.AboutBlock}>
      <Box flex={1} className={css.AboutBlock__block}>
        {about.video_link && (
          <>
            <div className={css.AboutBlock__videoWrap} onClick={modal.onOpen}>
              <Photo className={css.video} src={about.video_preview!} alt="about video" />
              <Button aria-label="play" className={css.playBtn}><PlayIcon /></Button>
            </div>
            <VideoDialog src={about.video_link} open={modal.open} onClose={modal.onClose} />
          </>
        )}
        {/*<Typography>*/}
        {/*  {t('main.forum_organizer')} — <Link*/}
        {/*  to={{ pathname: FOOTER.resources.foundation }} target="_blank" referrerPolicy="no-referrer" rel="noreferrer"*/}
        {/*  className={css.AboutBlock__altText}>{t('main.fund_naked_heart')}</Link>*/}
        {/*</Typography>*/}
        <Typography className={css.AboutBlock__videoTitle} component="div"
                    dangerouslySetInnerHTML={{ __html: videoTitle ?? '' }} />
        <Box display="flex">
          {counts.map((i, ind) => (
            <Box key={ind} className={css.AboutBlock__countItem}>
              <Typography variant="h2" color="inherit">{i.title}</Typography>
              <Typography>{i.subtitle}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box flex={1} className={css.AboutBlock__block}>
        <UnderlinedText variant="h2" color="inherit">{t('main.about_forum')}</UnderlinedText>
        <Typography className={css.AboutBlock__text} dangerouslySetInnerHTML={{ __html: description || '' }} />
      </Box>
    </Box>
  );
}

function SpeakerList() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const pageMargin = usePageMargin();
  let speakers = useSelector(speakersSelector);
  const [detailedItem, setDetailedItem] = useState<Speaker>();
  const handleDetailedOpen = useCallback((item: Speaker) => {
    setDetailedItem(item);
  }, []);
  const handleDetailedClose = useCallback(() => {
    setDetailedItem(undefined);
  }, []);
  if (!speakers || speakers.length === 0) return null;
  speakers = speakers.slice(0, 5);
  return (
    <RowBlock className={css.SpeakerList} title={t('main.speakers')}
              action={<RightArrowButton href={SPEAKERS_PAGE}>{t('main.all_speakers')}</RightArrowButton>}>
      <div className={css.SpeakerList__content}>
        <div className={css.SpeakerList__list}>
          {!isMobile && speakers.map((i, ind) => (
            <SpeakerCard key={ind} className={css.SpeakerCard} item={i} onClick={() => handleDetailedOpen(i)} />
          ))}
        </div>

        {isMobile && (
          <Swiper
            className={css.Swiper}
            watchSlidesProgress={true} watchSlidesVisibility={true} slidesPerView="auto"
            slidesOffsetBefore={pageMargin}
            slidesOffsetAfter={pageMargin}
            spaceBetween={16}
            touchStartPreventDefault={false}
          >
            {speakers.map((i, ind) => (
              <SwiperSlide key={ind} className={css.SwiperSlide}>
                <SpeakerCard className={css.SpeakerCard} item={i} onClick={() => handleDetailedOpen(i)} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
      <SpeakerDetailedModal
        open={!!detailedItem} onClose={handleDetailedClose} speaker={detailedItem}
        // onPrev={hasDetailedPrev ? handleDetailedPrev : undefined}
        // onNext={hasDetailedNext ? handleDetailedNext : undefined}
      />
    </RowBlock>
  );
}

function Photos() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const images = useSelector(galleryImagesSelector);
  if (images.length === 0) return null;
  const initialIndex = (!isMobile && images.length > 1) ? 1 : 0;
  return (
    <RowBlock className={css.PhotosList} title={t('main.gallery')}
              action={<RightArrowButton href={GALLERY_PAGE}>{t('main.all_photos')}</RightArrowButton>}>
      <GallerySwiper
        className={css.PhotosSwiper}
        images={images}
        slideIndex={initialIndex}
      />
    </RowBlock>
  );
}

function PartnerList() {
  const { t } = useTranslation();
  const partners = useSelector(partnersSelector);
  if (partners.length === 0) return null;
  return (
    <RowBlock className={css.PartnerList} title={t('main.partners')}>
      <div className={css.PartnerList__content}>
        {partners.map(i => {
          const Wrapper = i.link ? Link : Box;
          return (
            <Wrapper className={css.PartnerList__partner} key={i.id} aria-label="partner_logo"
                     to={{ pathname: i.link }} target="_blank" referrerPolicy="no-referrer" rel="noreferrer">
              <Image src={i.logo} alt={'partner_logo'} />
            </Wrapper>
          );
        })}
      </div>
    </RowBlock>
  );
}

function UnderlinedHeader({ className, children }: Elem) {
  return (<UnderlinedText className={className} variant="h2" color='secondary'>{children}</UnderlinedText>);
}

export function RowBlock({ className, title, action, children }: Elem & { title: ReactNode, action?: ReactNode }) {
  const isMobile = useIsMobile();
  return (
    <div className={classnames(css.RowBlock, className)}>
      <div className={css.RowBlock__header}>
        <UnderlinedHeader>{title}</UnderlinedHeader>
        {!isMobile && action}
      </div>
      {children}
      {isMobile && (<div className={css.RowBlock__bottomActions}>{action}</div>)}
    </div>
  );
}

function useModalState() {
  const [open, setOpen] = useState(false);
  const handleSwitch = useCallback(() => {
    setOpen(!open);
  }, [open]);
  return { open, onOpen: handleSwitch, onClose: handleSwitch };
}

function VideoDialog({ src, open, onClose }: { src: string, open: boolean, onClose: () => void }) {
  return (
    <Dialog className={css.VideoDialog} open={open} onClose={onClose}>
      <ReactPlayer className={css.player} width="" height="" controls url={src} />
    </Dialog>
  );
}
